import type { CheckRequestError, CheckRequestStatus } from '~/utils/geocomply';

type LocationCountryName = 'united states' | 'canada';
type CachedLocationData = {
  locationCountry?: string;
  locationState?: string;
  locationStatus?: CheckRequestStatus;
  locationErrors?: CheckRequestError[];
  timestamp: number;
  source: 'geocomply' | 'geoip';
};

type CachedGeoIPLocation = {
  country: string;
  timestamp: number;
};

const LOCATION_CACHE_KEY = 'orb_location_cache';
const GEOIP_CACHE_KEY = 'orb_geoip_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

const isCacheValid = (timestamp: number): boolean => Date.now() - timestamp < CACHE_DURATION;

const isAllowedLocation = (country: string): country is LocationCountryName => {
  const allowedLocations: LocationCountryName[] = ['united states', 'canada'];
  return allowedLocations.includes(country as LocationCountryName);
};

const clearLocationCache = (): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    localStorage.removeItem(LOCATION_CACHE_KEY);
  } catch {
    // swallow
  }
};

const clearGeoIPCache = (): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    localStorage.removeItem(GEOIP_CACHE_KEY);
  } catch {
    // swallow
  }
};

const getCachedLocationData = (): CachedLocationData | null => {
  if (!isLocalStorageAvailable()) return null;

  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;

    const data: CachedLocationData = JSON.parse(cached);

    if (!isCacheValid(data.timestamp)) {
      clearLocationCache();
      return null;
    }

    return data;
  } catch {
    clearLocationCache();
    return null;
  }
};

const setCachedLocationData = (
  locationData: {
    locationCountry?: string;
    locationState?: string;
    locationStatus?: CheckRequestStatus;
    locationErrors?: CheckRequestError[];
  },
  source: 'geocomply' | 'geoip' = 'geocomply'
): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    const cacheData: CachedLocationData = {
      ...locationData,
      source,
      timestamp: Date.now(),
    };

    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // swallow
  }
};

const getCachedGeoIPLocation = (): CachedGeoIPLocation | null => {
  if (!isLocalStorageAvailable()) return null;

  try {
    const cached = localStorage.getItem(GEOIP_CACHE_KEY);
    if (!cached) return null;

    const data: CachedGeoIPLocation = JSON.parse(cached);

    if (!isCacheValid(data.timestamp)) {
      clearGeoIPCache();
      return null;
    }

    return data;
  } catch {
    clearGeoIPCache();
    return null;
  }
};

const setCachedGeoIPLocation = (country: string): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    const cacheData: CachedGeoIPLocation = {
      country,
      timestamp: Date.now(),
    };

    localStorage.setItem(GEOIP_CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // swallow
  }
};

const getBestAvailableLocationCountry = (): LocationCountryName => {
  const geocomplyCache = getCachedLocationData();
  if (geocomplyCache?.locationCountry) {
    const country = geocomplyCache.locationCountry.toLowerCase() as LocationCountryName;
    if (isAllowedLocation(country)) {
      return country;
    }
  }

  const geoipCache = getCachedGeoIPLocation();
  if (geoipCache?.country) {
    const country = geoipCache.country.toLowerCase() as LocationCountryName;
    if (isAllowedLocation(country)) {
      return country;
    }
  }

  return 'united states';
};

const clearAllCaches = (): void => {
  clearLocationCache();
  clearGeoIPCache();
};

const locationStorage = {
  getCachedLocationData,
  setCachedLocationData,
  clearLocationCache,
  getCachedGeoIPLocation,
  setCachedGeoIPLocation,
  clearGeoIPCache,
  getBestAvailableLocationCountry,
  clearAllCaches,
};

export { locationStorage };
export type { CachedLocationData, CachedGeoIPLocation, LocationCountryName };
